// Coverage Map — document-structure wire shapes (Decision Surface plan).
// Mirrors src/ai_alm/api/routes/document_structure.py.

export type SectionStateKind = 'filled' | 'empty_optional' | 'empty_required'

export interface CoverageTemplate {
  /** 'A'..'E' for a system-default template; null for custom/extracted. */
  code: string | null
  /** e.g. "System Default A". */
  label: string
  audience: string | null
  /** _resolve_template origin (user_supplied_file / intake / system_default:…). */
  origin: string
}

export interface CoverageSectionRow {
  id: string
  /** The exporter's real heading — rendered verbatim (D4). */
  heading: string
  required: boolean
  target_content: string[]
  /** Will render content (resolved target, xref, withheld stub, or a
   *  diagram/wireframe whose source .mmd exists). */
  present: boolean
  state: SectionStateKind
  /** empty_required_section | unknown_target_content | null. */
  misfit_kind: string | null
}

export interface ComplianceInfo {
  /** Regulatory flags declared in the intake. */
  profiles: string[]
  /** True when ≥1 flag has a structured profile (honest "driving" claim). */
  profiled: boolean
  /** Regulator-driven requirements injected into the document. */
  injected_requirement_count: number
}

export interface DocumentStructureResponse {
  /** "forecast" (decision time) | "authoritative" (a completed export exists). */
  basis: string
  template: CoverageTemplate
  sections: CoverageSectionRow[]
  compliance: ComplianceInfo
  /**
   * Stage 3.5 — content tokens produced (artifact non-empty) but not
   * claimed by any section of the resolved template: generated content
   * this template won't place in the document. Empty for most runs.
   */
  orphan_content: string[]
}

// Plain-language help, keyed by section id (stable across templates;
// numeric heading prefixes are not). Shown as a subtitle/tooltip — never
// a replacement for the real heading (D4). Absent id → no subtitle.
export const SECTION_PLAIN_LANGUAGE: Record<string, string> = {
  'executive-summary': 'The one-page overview for leadership.',
  glossary: 'Definitions of the terms used in this document.',
  'intent-map': 'What you set out to build and why.',
  'deferred-unknowns': "Open questions you've parked for later.",
  'constraint-log': 'The hard limits this design must respect.',
  brd: 'What the business needs (business requirements).',
  srd: 'What the system must do (system requirements).',
  'endpoint-surface': 'The API endpoints the system exposes.',
  diagrams: 'Architecture diagrams.',
  wireframes: 'Screen mockups.',
  compliance: 'Compliance posture and risks.',
  'data-classification': 'How sensitive data is classified + privacy impact.',
  'risk-register': 'Tracked risks and their mitigations.',
  'threat-model': 'Security threat analysis (STRIDE) per asset.',
  'asvs-mapping': 'Security-verification checklist coverage.',
  'control-matrix': 'How each regulatory control is met.',
  'ai-governance': 'AI governance posture.',
  traceability: 'Each requirement traced to its source.',
  references: 'Source documents and standards cited.',
  // Audience-specific + appendix sections (executive / procurement /
  // auditor recipes) — completes A–E coverage (legibility pass).
  summary: 'A short overview of the document.',
  problem: 'The problem this project sets out to solve.',
  goals: 'What the project is trying to achieve.',
  scope: "What's in scope — and what's deliberately out.",
  constraints: 'The hard limits this design must respect.',
  interfaces: 'The external interfaces the system talks to.',
  'rate-limits-brief': 'API rate-limit and throttling policy.',
  risks: 'Tracked risks and their mitigations.',
  audit: 'The audit trail of changes and quality checks.',
  rqs: 'The requirement-quality score for this document.',
  provenance: 'Where each part of the document came from.',
  'srd-nfr': 'Non-functional (quality) requirements.',
  'srd-technical-refinements': 'Detailed technical requirement refinements.',
  'appendix-data-dictionary': 'Appendix: the full data dictionary.',
  'appendix-deployment': 'Appendix: the deployment manifest.',
  'appendix-formulas': 'Appendix: formula and calculation definitions.',
  'appendix-algorithm': 'Appendix: algorithm details.',
  'appendix-glossary-proposals': 'Appendix: proposed glossary additions.',
  'appendix-srd-refinements': 'Appendix: detailed requirement refinements.',
}
